import React, { useState, useContext } from 'react';
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Send } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { submitEnquiry } from '../../services/api';
import EnquiryContext from '../../context/EnquiryContext';

const validationSchema = yup.object({
  studentName: yup.string().required('Name is required').min(3, 'Name must be at least 3 characters'),
  contactNo: yup
    .string()
    .required('Contact number is required')
    .matches(/^[0-9]{10}$/, 'Enter a valid 10-digit number'),
  email: yup.string().email('Enter a valid email').required('Email is required'),
  courseInterestedIn: yup.string().required('Please select a course'),
  message: yup.string(),
});

const EnquiryForm = ({ variant = 'default', onSuccess }) => {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const enquiryContext = useContext(EnquiryContext);

  const formik = useFormik({
    initialValues: {
      studentName: '',
      contactNo: '',
      email: '',
      courseInterestedIn: '',
      message: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        setError('');
        await submitEnquiry(values);
        setSubmitted(true);
        resetForm();
        // Call onSuccess callback if provided (used by PopupEnquiryModal)
        if (onSuccess) {
          onSuccess(values);
        }
        // If a global enquiry context is available, show the success modal with details
        if (enquiryContext && enquiryContext.showEnquirySuccess) {
          enquiryContext.showEnquirySuccess(values);
        }
        setTimeout(() => setSubmitted(false), 5000);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to submit. Please try again.');
      }
    },
  });

  const courses = ['MBBS'];

  // Shared wrapper styles for background and borders
  const sharedWrapperStyles = {
    mb: 2,
    bgcolor: '#FFFFFF',
    borderRadius: 1,
    '& .MuiInputLabel-root': {
      color: '#555555',
    },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: 'primary.main',
        borderWidth: 2,
      },
      '&:hover fieldset': {
        borderColor: 'primary.light',
      },
    },
  };

  // Explicit inline styles for the native HTML input to force cursor visibility
  const nativeInputStyles = {
    style: {
      caretColor: '#8a211d', // Forces a dark cursor
      color: '#2f2522',      // Forces dark text
      fontWeight: 500,
    }
  };

  return (
    <Box>
      {variant === 'default' && (
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
          Get Free Counselling
        </Typography>
      )}

      {submitted && variant === 'default' && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          Thank you! We will contact you shortly.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={formik.handleSubmit}>
        <TextField
          fullWidth
          name="studentName"
          label="Full Name"
          value={formik.values.studentName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.studentName && Boolean(formik.errors.studentName)}
          helperText={formik.touched.studentName && formik.errors.studentName}
          sx={sharedWrapperStyles}
          size={variant === 'popup' ? 'small' : 'medium'}
          inputProps={nativeInputStyles} // Applies styles directly to <input>
        />

        <TextField
          fullWidth
          name="contactNo"
          label="Contact Number"
          value={formik.values.contactNo}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.contactNo && Boolean(formik.errors.contactNo)}
          helperText={formik.touched.contactNo && formik.errors.contactNo}
          sx={sharedWrapperStyles}
          size={variant === 'popup' ? 'small' : 'medium'}
          inputProps={nativeInputStyles}
        />

        <TextField
          fullWidth
          name="email"
          label="Email Address"
          type="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          sx={sharedWrapperStyles}
          size={variant === 'popup' ? 'small' : 'medium'}
          inputProps={nativeInputStyles}
        />

        <FormControl 
          fullWidth 
          sx={sharedWrapperStyles} 
          error={formik.touched.courseInterestedIn && Boolean(formik.errors.courseInterestedIn)}
        >
          <InputLabel size={variant === 'popup' ? 'small' : 'normal'}>Course Interested In</InputLabel>
          <Select
            name="courseInterestedIn"
            value={formik.values.courseInterestedIn}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Course Interested In"
            size={variant === 'popup' ? 'small' : 'medium'}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: '#1a1a1a',
                  color: '#ffffff',
                  '& .MuiMenuItem-root': {
                    color: '#ffffff',
                    '&:hover': {
                      bgcolor: '#333333',
                    },
                    '&.Mui-selected': {
                      bgcolor: '#444444',
                      '&:hover': {
                        bgcolor: '#555555',
                      },
                    },
                  },
                },
              },
            }}
            sx={{
              color: '#2f2522',
              '& .MuiSelect-icon': { color: '#2f2522' },
            }}
          >
            {courses.map((course) => (
              <MenuItem key={course} value={course}>
                {course}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          name="message"
          label="Message (Optional)"
          multiline
          rows={variant === 'popup' ? 2 : 3}
          value={formik.values.message}
          onChange={formik.handleChange}
          sx={{ ...sharedWrapperStyles, mb: 2.5 }}
          size={variant === 'popup' ? 'small' : 'medium'}
          inputProps={nativeInputStyles} // Also applies to the <textarea> inside multiline
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          startIcon={formik.isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
          disabled={formik.isSubmitting}
          sx={{
            py: variant === 'popup' ? 1.2 : 1.5,
            fontSize: variant === 'popup' ? '0.95rem' : '1.1rem',
            borderRadius: 2,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 14px rgba(138,33,29,0.3)',
            '&:hover': {
              boxShadow: '0 6px 20px rgba(138,33,29,0.4)',
            },
          }}
        >
          {formik.isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
        </Button>
      </form>
    </Box>
  );
};

export default EnquiryForm;